import { z } from 'zod';

/** Logo da empresa no PDF: PNG ou JPEG (nunca SVG), no maximo 512 KB. A API valida os magic bytes e o tamanho; o banco repete o limite. */
export const LOGO_MAX_BYTES = 512 * 1024;
export const LOGO_MIMES = ['image/png', 'image/jpeg'] as const;
export type LogoMime = (typeof LOGO_MIMES)[number];

/** CNPJ valido pelos dois digitos verificadores (14 digitos; rejeita sequencias repetidas). Aritmetica de cadastro, nao financeira. */
export function cnpjValido(valor: string): boolean {
  const d = valor.replace(/\D/g, '');
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const dv = (base: string): number => {
    const pesos = base.length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma = [...base].reduce((acc, c, i) => acc + Number(c) * (pesos[i] as number), 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  const d1 = dv(d.slice(0, 12));
  const d2 = dv(d.slice(0, 12) + String(d1));
  return d.endsWith(`${d1}${d2}`);
}

export const REGIMES_TRIBUTARIOS = ['SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL'] as const;
export type RegimeTributario = (typeof REGIMES_TRIBUTARIOS)[number];
export const ROTULO_REGIME: Record<RegimeTributario, string> = {
  SIMPLES_NACIONAL: 'Simples Nacional',
  LUCRO_PRESUMIDO: 'Lucro Presumido',
  LUCRO_REAL: 'Lucro Real',
};

const texto = (max: number) => z.string().trim().max(max, `Use no maximo ${max} caracteres`);
/** Vazio e aceito (a API grava null); a API tira a mascara (so digitos) e poe a UF em caixa alta. */
const comMascara = (re: RegExp, msg: string) => z.string().trim().refine((v) => v === '' || re.test(v), msg);

/**
 * PUT /empresa/perfil: dados do cabecalho do PDF do orcamento. Texto vazio = campo limpo (null).
 * Entrada = saida (sem transform), para o mesmo schema servir ao react-hook-form no front.
 */
export const empresaPerfilSchema = z.object({
  razaoSocial: texto(200),
  nomeFantasia: texto(200),
  cnpj: z.string().trim().refine((v) => v === '' || cnpjValido(v), 'CNPJ invalido (confira os 14 digitos)'),
  /** Digitos (2 a 14) ou ISENTO; vazio limpa. */
  inscricaoEstadual: z
    .string()
    .trim()
    .refine((v) => v === '' || /^(\d{2,14}|isento)$/i.test(v.replace(/[.\-/\s]/g, '')), 'Inscricao estadual invalida (so digitos ou ISENTO)'),
  inscricaoMunicipal: z
    .string()
    .trim()
    .refine((v) => v === '' || /^\d{1,20}$/.test(v.replace(/[.\-/\s]/g, '')), 'Inscricao municipal invalida (so digitos)'),
  /** Vazio = nao informado. Cadastro apenas: nenhuma formula usa o regime. */
  regimeTributario: z.enum([...REGIMES_TRIBUTARIOS, '']),
  contatoNome: texto(120),
  endereco: texto(200),
  cidade: texto(100),
  uf: comMascara(/^[A-Za-z]{2}$/, 'UF invalida (2 letras)'),
  cep: comMascara(/^\d{5}-?\d{3}$/, 'CEP invalido (8 digitos)'),
  telefone: texto(40),
  email: texto(200).refine((v) => v === '' || z.email().safeParse(v).success, 'E-mail invalido'),
  site: texto(200),
  rodapePdf: texto(500),
  /** "Sobre a empresa": mercado, principais clientes, estrategia de preco, sazonalidade. Alimenta o contexto fixo da IA. */
  sobre: texto(2000).optional(),
});
export type EmpresaPerfilDto = z.infer<typeof empresaPerfilSchema>;

/** Campos de texto do perfil como a API devolve: null = nao informado. `cnpj` e `cep` so digitos. */
export interface EmpresaPerfilResponse {
  empresaId: string;
  /** Nome cadastrado da empresa (usado no PDF quando a razao social nao foi informada). */
  empresaNome: string;
  razaoSocial: string | null;
  nomeFantasia: string | null;
  cnpj: string | null;
  endereco: string | null;
  cidade: string | null;
  uf: string | null;
  cep: string | null;
  telefone: string | null;
  email: string | null;
  site: string | null;
  inscricaoEstadual: string | null;
  inscricaoMunicipal: string | null;
  regimeTributario: RegimeTributario | null;
  contatoNome: string | null;
  rodapePdf: string | null;
  sobre: string | null;
  temLogo: boolean;
  logoMime: LogoMime | null;
  /** null = perfil ainda nao criado. */
  atualizadoEm: string | null;
}
